import React from 'react';
import UsersIcon from '@heroicons/react/24/solid/UsersIcon';
import DocsIcon from '@heroicons/react/24/solid/DocumentDuplicateIcon';
import ChartBarIcon from '@heroicons/react/24/solid/ChartBarIcon';
import BriefcaseIcon from '@heroicons/react/24/solid/BriefcaseIcon';
import LightBulbIcon from '@heroicons/react/24/solid/LightBulbIcon';
import Cog6ToothIcon from '@heroicons/react/24/solid/Cog6ToothIcon';
import BookmarkIcon from '@heroicons/react/24/solid/BookmarkIcon';
import BuildingOffice2Icon from '@heroicons/react/24/solid/BuildingOffice2Icon';
import MapPinIcon from '@heroicons/react/24/solid/MapPinIcon';
import WrenchScrewdriverIcon from '@heroicons/react/24/solid/WrenchScrewdriverIcon';
import DocumentMagnifyingGlassIcon from '@heroicons/react/24/solid/DocumentMagnifyingGlassIcon';
import GlobeAltIcon from '@heroicons/react/24/solid/GlobeAltIcon';
import { SvgIcon } from '@mui/material';
import FlagIcon from '@heroicons/react/24/solid/FlagIcon';
import InboxIcon from '@heroicons/react/24/solid/InboxIcon';
import ArrowsRightLeftIcon from '@heroicons/react/24/solid/ArrowsRightLeftIcon';
import UserPlusIcon from '@heroicons/react/24/solid/UserPlusIcon';
import SparklesIcon from '@heroicons/react/24/solid/SparklesIcon';
import ChatBubbleLeftRightIcon from '@heroicons/react/24/solid/ChatBubbleLeftRightIcon';
import TicketIcon from '@heroicons/react/24/solid/TicketIcon';

import type { ReactElement } from 'react';

export interface NavItem {
  title: string,
  path: string,
  icon: ReactElement,
  mainResource: string[],
}

export const items: NavItem[] = [
  {
    title: 'People',
    path: '/people/list/',
    icon: (
      <SvgIcon fontSize="small">
        <UsersIcon />
      </SvgIcon>
    ),
    mainResource: ['person'],
  },
  {
    title: 'Projects',
    path: '/projects/list/',
    icon: (
      <SvgIcon fontSize="small">
        <DocsIcon />
      </SvgIcon>
    ),
    mainResource: ['project'],
  },
  {
    title: 'Project Transfer',
    path: '/projects/transfer/',
    icon: (
      <SvgIcon fontSize="small">
        <ArrowsRightLeftIcon />
      </SvgIcon>
    ),
    mainResource: ['project_transfer'],
  },
  {
    title: 'Pitches',
    path: '/pitches/list/',
    icon: (
      <SvgIcon fontSize="small">
        <LightBulbIcon />
      </SvgIcon>
    ),
    mainResource: ['pitches'],
  },
  {
    title: 'Future joiners',
    path: '/future-joiners/list/',
    icon: (
      <SvgIcon fontSize="small">
        <UserPlusIcon />
      </SvgIcon>
    ),
    mainResource: ['future_joiner'],
  },
  {
    title: 'Profile Generator',
    path: '/profile-generator/',
    icon: (
      <SvgIcon fontSize="small">
        <SparklesIcon />
      </SvgIcon>
    ),
    mainResource: ['profile_creator_user'],
  },
  {
    title: 'LLM Feedback',
    path: '/llm-feedback/',
    icon: (
      <SvgIcon fontSize="small">
        <ChatBubbleLeftRightIcon />
      </SvgIcon>
    ),
    mainResource: ['llm_feedback'],
  },
  {
    title: 'AI Credits',
    path: '/ai-credits/',
    icon: (
      <SvgIcon fontSize="small">
        <TicketIcon />
      </SvgIcon>
    ),
    mainResource: ['ai_credits_admin'],
  },
  {
    title: 'Clients',
    path: '/clients/list/',
    icon: (
      <SvgIcon fontSize="small">
        <BriefcaseIcon />
      </SvgIcon>
    ),
    mainResource: ['client'],
  },
  {
    title: 'Departments',
    path: '/departments/list/',
    icon: (
      <SvgIcon fontSize="small">
        <BuildingOffice2Icon />
      </SvgIcon>
    ),
    mainResource: ['department'],
  },
  {
    title: 'Tasks',
    path: '/tasks/list/',
    mainResource: ['tasks'],
    icon: (
      <SvgIcon fontSize="small">
        <InboxIcon />
      </SvgIcon>
    ),
  },
  {
    title: 'Bookmarks',
    path: '/bookmarks/list/',
    mainResource: ['bookmark'],
    icon: (
      <SvgIcon fontSize="small">
        <BookmarkIcon />
      </SvgIcon>
    ),
  },
  {
    title: 'Documents',
    path: '/documents/list/',
    // Visible to anyone with read on project_info OR infosec — permission
    // checks on individual rows happen server-side via type-as-resource.
    mainResource: ['project_info', 'infosec'],
    icon: (
      <SvgIcon fontSize="small">
        <DocsIcon />
      </SvgIcon>
    ),
  },
  {
    title: 'Dashboards',
    path: '/dashboards/',
    // Gated on the dedicated `dashboards` resource (default Deny).
    // Granting it exposes the whole Dashboards section; row-level data
    // each dashboard renders is still subject to the underlying
    // resource permissions (e.g. project read for the cheat sheet).
    mainResource: ['dashboards'],
    icon: (
      <SvgIcon fontSize="small">
        <ChartBarIcon />
      </SvgIcon>
    ),
  },
  {
    title: 'Checkpoint',
    path: '/checkpoint/',
    mainResource: ['checkpoint'],
    icon: (
      <SvgIcon fontSize="small">
        <FlagIcon />
      </SvgIcon>
    ),
  },
  {
    title: 'People Map',
    path: '/people_map/',
    mainResource: ['people_map'],
    icon: (
      <SvgIcon fontSize="small">
        <MapPinIcon />
      </SvgIcon>
    ),
  },
  {
    title: 'Skills Search',
    path: '/people/skill-search/',
    mainResource: ['skills_search'],
    icon: (
      <SvgIcon fontSize="small">
        <WrenchScrewdriverIcon />
      </SvgIcon>
    ),
  },
  {
    title: 'Holidays',
    path: '/internal/internal_holidays/',
    icon: (
      <SvgIcon fontSize="small">
        <GlobeAltIcon />
      </SvgIcon>
    ),
    mainResource: ['holidays_my_projects'],
  },
  {
    title: 'Internal',
    path: '/internal/internal_index/',
    icon: (
      <SvgIcon fontSize="small">
        <DocumentMagnifyingGlassIcon />
      </SvgIcon>
    ),
    mainResource: ['internal_smarttime_bob', 'internal_unallocated_people', 'internal_finance', 'internal_5'],
  },
  {
    title: 'Settings',
    path: '/settings/settings/',
    icon: (
      <SvgIcon fontSize="small">
        <Cog6ToothIcon />
      </SvgIcon>
    ),
    mainResource: ['settings'],
  },

];
